import React from 'react';

const CurriculumPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-100/40 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto text-center space-y-12">
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-relaxed">
              スピリアルコンシェルジュアカデミー
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 font-light leading-relaxed">
              3か月カリキュラム概要
            </p>
          </div>

          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/65552919c2526e8d48a131d6c46d348b.jpg"
              alt="3か月の旅路"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border-2 border-amber-200 shadow-lg space-y-6 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl font-bold text-gray-900">
              このアカデミーは、<br />
              知識を学ぶための講座ではありません。
            </p>
            <p>
              Light Workerとして<br />
              「場を支え、光を現実に届ける役割」を<br />
              引き受けられるかを確認し、育てる3か月です。
            </p>
          </div>
        </div>
      </section>

      {/* Month 1 */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-slate-800 text-white px-8 py-3 rounded-full text-xl font-bold mb-6">
              1か月目
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              基礎構築フェーズ
            </h2>
            <p className="text-xl md:text-2xl text-amber-800 font-light">
              ― 役割の現実を理解する ―
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">テーマ</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                「運営とは何か」を<br />
                理想ではなく現実として理解する
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">主な内容</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>スピリアルコンシェルジュの役割と責任</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>表に立つ人と、場を支える人の違い</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>ダイナビジョン事務局の全体構造</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>講師・参加者対応の基本姿勢</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>事務局・運営に必要な基礎OAスキル</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border-2 border-amber-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">この段階で起きること</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">「思っていたのと違う」と感じる人が出る</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">支える仕事への幻想が外れる</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">本当に引き受けたいかを自分に問い始める</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Month 2 */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-slate-700 text-white px-8 py-3 rounded-full text-xl font-bold mb-6">
              2か月目
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              実務理解フェーズ
            </h2>
            <p className="text-xl md:text-2xl text-amber-800 font-light">
              ― 判断を伴う運営に触れる ―
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">テーマ</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                作業ではなく「判断」が求められる<br />
                役割を体感する
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">主な内容</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>セミナー・LIVE運営の裏側</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>開催前・当日・事後のリアルな動き</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>実際に起きたトラブル事例のケーススタディ</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>講師・参加者とのコミュニケーション</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>AI・ツールを「補助」として使う実務的な考え方</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border-2 border-amber-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">この段階で起きること</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">正解が一つではないことを知る</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">責任を伴う判断の重みを理解する</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">「自分ならどうするか」を考え始める</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Month 3 */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-slate-600 text-white px-8 py-3 rounded-full text-xl font-bold mb-6">
              3か月目
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              適性判断フェーズ
            </h2>
            <p className="text-xl md:text-2xl text-amber-800 font-light">
              ― この役割を生きるかを決める ―
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">テーマ</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                覚悟と現実対応力の最終確認
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">主な内容</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>実務想定ワーク（優先順位・判断・対応）</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>責任を引き受けるとはどういうことか</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>自分に向いている役割・向いていない役割の整理</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>個別フィードバック</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-xl text-slate-700 mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                  <span>最終レポート（続ける／見送るの決断）</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border-2 border-amber-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">この段階で起きること</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">「続けたい」「今は違う」どちらも明確になる</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">自分の限界と可能性を正しく理解できる</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-sm text-center">
                <p className="text-gray-800">役割として関わる覚悟が定まる</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* After Completion */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            修了後について
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl text-center">
              アカデミー修了者の中から、<br />
              適性・タイミングが合う方にのみ、
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-8 rounded-xl border-2 border-slate-200 shadow-sm text-center">
                <i className="ri-building-line text-4xl text-slate-700 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">ダイナビジョン事務局としての実務参加</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-slate-200 shadow-sm text-center">
                <i className="ri-briefcase-line text-4xl text-slate-700 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">アルバイト／業務委託／正社員候補</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-slate-200 shadow-sm text-center">
                <i className="ri-team-line text-4xl text-slate-700 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">継続的なプロジェクト参画</p>
              </div>
            </div>

            <p className="text-xl text-center pt-8">
              など、<br />
              現実の仕事へとつながる可能性があります。
            </p>

            <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border-2 border-amber-200 shadow-lg mt-8">
              <p className="text-center text-gray-700 font-bold">
                ※全員が仕事につながるわけではありません<br />
                ※「見送る」という判断も尊重されます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            このアカデミーで得られるもの
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg text-center">
              <i className="ri-lightbulb-line text-5xl text-slate-700 mb-6 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <p className="text-xl text-gray-800 font-bold">スピリチュアルを現実で機能させる視点</p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg text-center">
              <i className="ri-compass-line text-5xl text-slate-700 mb-6 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <p className="text-xl text-gray-800 font-bold">人と場を支えるための判断力</p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg text-center">
              <i className="ri-user-heart-line text-5xl text-slate-700 mb-6 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <p className="text-xl text-gray-800 font-bold">Light Workerとしての在り方</p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl border-2 border-slate-200 shadow-lg text-center">
              <i className="ri-shield-check-line text-5xl text-slate-700 mb-6 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <p className="text-xl text-gray-800 font-bold">仕事にも人生にも使える現実対応力</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            このアカデミーは、こんな方のためのものです
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <p className="text-gray-800 text-lg">表舞台に光をともす仕事を引き受けたい</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <p className="text-gray-800 text-lg">スピリチュアルを「役割」として生きたい</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <p className="text-gray-800 text-lg">責任ある立場で成長したい</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <p className="text-gray-800 text-lg">楽な学びではなく、確かな道を選びたい</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            この道を選ぶかどうかは、<br />あなた次第です
          </h2>

          <a 
            href="/spcacademy/entry202601"
            className="inline-block bg-slate-800 hover:bg-slate-900 text-white font-bold text-lg px-12 py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            募集ページに戻る
          </a>
        </div>
      </section>
    </div>
  );
};

export default CurriculumPage;
